import { useContext, useEffect, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import ZillowMapContext from '@zillow/zillow-map-control/dist/ZillowMapContext';

interface SimpleCircleMapDotProps {
  lat: number;
  lng: number;
  color?: string;
  size?: number;
  zIndex?: number;
  popoverTitle?: string;
  popoverContent?: string | React.ReactNode;
  popoverHeaderAction?: React.ReactNode;
  onOpen?: () => void;
}

const createCircleIcon = (color: string, size: number): string => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 1}" fill="${color}" stroke="#111116" stroke-width="1" />
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const createPopoverHeaderElement = (title: string, headerAction?: React.ReactNode): HTMLDivElement => {
  const container = document.createElement('div');
  container.style.cssText = `
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-width: 240px;
  `;

  const titleEl = document.createElement('div');
  titleEl.style.cssText = `
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: #111116;
    flex: 1;
  `;
  titleEl.textContent = title;
  container.appendChild(titleEl);

  if (headerAction) {
    const actionEl = document.createElement('div');
    actionEl.style.cssText = `
      display: flex;
      align-items: center;
      flex-shrink: 0;
    `;
    actionEl.innerHTML = renderToStaticMarkup(<>{headerAction}</>);
    container.appendChild(actionEl);
  }

  return container;
};

const createPopoverContent = (content: string | React.ReactNode, hasHeader: boolean): string => {
  const bodyContent = typeof content === 'string' ? content : renderToStaticMarkup(<>{content}</>);

  const dividerHtml = hasHeader
    ? `<div style="
        height: 1px;
        background-color: #e5e5e5;
        margin: 0 -12px 12px -12px;
      "></div>`
    : '';

  return `
    <style>
      .gm-style-iw-tc { display: none !important; }
      .gm-style-iw-d { overflow: visible !important; padding: 12px !important; }
      .gm-style-iw-ch { padding-top: 12px !important; padding-bottom: 0 !important; }
    </style>
    <div style="
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      min-width: 240px;
      max-width: 320px;
      font-size: 14px;
      color: #111116;
      line-height: 1.5;
      margin-inline-start: -12px;
      margin-block-start: -16px;
      min-width: calc(100% + 12px);
    ">
      ${dividerHtml}
      ${bodyContent}
    </div>
  `;
};

export const SimpleCircleMapDot = ({
  lat,
  lng,
  color = '#999999',
  size = 12,
  zIndex,
  popoverTitle,
  popoverContent,
  popoverHeaderAction,
  onOpen,
}: SimpleCircleMapDotProps) => {
  const { gmap } = useContext<{ gmap: google.maps.Map }>(ZillowMapContext);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  useEffect(() => {
    if (!gmap || !window.google?.maps?.Data) return;

    const point = new google.maps.LatLng(lat, lng);

    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }

    const marker = new google.maps.Marker({
      position: point,
      ...(zIndex != null && { zIndex }),
      icon: {
        url: createCircleIcon(color, size),
        anchor: new google.maps.Point(size / 2, size / 2),
      },
    });

    marker.setMap(gmap);
    markerRef.current = marker;

    if (popoverContent) {
      const contentString = createPopoverContent(popoverContent, !!popoverTitle);

      const infoWindowOptions: google.maps.InfoWindowOptions = {
        content: contentString,
      };

      if (popoverTitle) {
        infoWindowOptions.headerContent = createPopoverHeaderElement(popoverTitle, popoverHeaderAction);
      }

      const infoWindow = new google.maps.InfoWindow(infoWindowOptions);

      infoWindowRef.current = infoWindow;

      marker.addListener('click', () => {
        onOpen?.();

        setTimeout(() => {
          infoWindow.open({
            anchor: marker,
            map: gmap,
          });
        }, 0);
      });

      function handleDocumentClick(event: MouseEvent) {
        const infoWindowContainer = document.querySelector('.gm-style-iw');
        if (infoWindowContainer && infoWindowContainer.contains(event.target as Node)) {
          return;
        }
        infoWindow.close();
      }

      document.addEventListener('click', handleDocumentClick);

      return () => {
        if (markerRef.current) {
          markerRef.current.setMap(null);
        }
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
        }
        document.removeEventListener('click', handleDocumentClick);
      };
    }

    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gmap, lat, lng, color, size, zIndex, popoverTitle]);

  useEffect(() => {
    if (infoWindowRef.current && popoverContent) {
      const contentString = createPopoverContent(popoverContent, !!popoverTitle);
      infoWindowRef.current.setContent(contentString);
    }
  }, [popoverContent, popoverTitle]);

  return null;
};
